import { ForgottenCheckinDto, ForgottenCheckoutDto, User, UserCreateDTO, UserStatus, UserUpdateDTO, WorkScheduleResponseDto, YearlyDetailsForEmployeeResponseDto, YearlyWorkStatsResponseDto, EligibleApprover } from "../../types/user.types";
import { Salary, SalaryUpdateDTO } from "../../types/salary.types";
import apiService from "../api/api.service";
import { UserBirthdaysResponseDto } from "../../types/user.types";
import assetService from "../asset/asset.service";
import { UpdateUserBySuperAdminDto } from "../../types/user.types";
import { BulkUpdateResult, BulkUserChanges } from "../../types/userBulk.types";

class UserService {
  private static instance: UserService;

  private constructor() { }

  public static getInstance(): UserService {
    if (!UserService.instance) {
      UserService.instance = new UserService();
    }
    return UserService.instance;
  }

  /**
   * Get all users
   */
  public async getUsers(): Promise<User[]> {
    const response = await apiService.get<User[]>(`/users`);
    return response;
  }

  public async getUsersByBranchId(companyId: number, branchId: number): Promise<User[]> {
    const response = await apiService.get<User[]>(`/users/branchId=${branchId}`);
    return response;
  }

  /**
   * Get a user by ID
   * @param id User ID
   */
  public async getUserById(id: number): Promise<User> {
    const response = await apiService.get<User>(`/users/${id}`);
    return response;
  }

  /**
   * Create a new user
   * @param data User data
   */
  public async createUser(data: UserCreateDTO): Promise<User> {
    const response = await apiService.post<User>("/users", data);
    //console.log(response);

    return response;
  }



  /**
   * Update an existing user
   * @param id User ID
   * @param data Updated user data
   */
  public async updateUser(id: number, data: any): Promise<User> {
    return await apiService.put(`/users/${id}`, data);
  }
  public async updateUserAdmin(id: number, data: UserUpdateDTO): Promise<User> {
    const response = await apiService.patch<User>(`/users/${id}`, data);
    return response;
  }

  /**
   * Seçilen çalışanlara toplu değişiklik uygular; sonuç çalışan bazında döner
   */
  public async bulkUpdateUsers(userIds: number[], changes: BulkUserChanges): Promise<BulkUpdateResult> {
    return await apiService.patch<BulkUpdateResult>(`/users/bulk`, { userIds, changes });
  }

  public async updateUserSuperAdmin(data: any): Promise<User> {
    return await apiService.put(`/users/superadmin`, data);
  }

  public async updateUserStatus(id: number, status: UserStatus): Promise<User> {
    // Eğer kullanıcı işten çıkarılıyorsa zimmet kontrolü yap
    if (status === UserStatus.RESIGNED || status === UserStatus.DELETED) {
      const assetCheck = await assetService.checkUserUnreturnedAssets(id);
      if (assetCheck.hasUnreturnedAssets) {
        const assetList = assetCheck.assets.map(asset => `• ${asset.name} (${asset.serialNumber})`).join('\n');
        throw new Error(
          `Bu kullanıcının ${assetCheck.assets.length} adet iade edilmemiş zimmeti bulunmaktadır:\n\n${assetList}\n\nİşten çıkarma işlemini gerçekleştirmek için önce tüm zimmetlerin iade edilmesi gerekmektedir.`
        );
      }
    }

    const response = await apiService.patch<User>(`/users/status/${id}`, { status });
    return response;
  }
  public async updateUserBySuperAdmin(id: number, data: Partial<UpdateUserBySuperAdminDto>): Promise<User> {
    const response = await apiService.patch<User>(`/super-admin/user/${id}`, data);
    return response;
  }
  /**
   * Delete a user
   * @param id User ID
   */
  public async deleteUser(id: number): Promise<void> {
    await apiService.delete(`/users/${id}`);
  }
  public async hardDeleteUser(id: number): Promise<void> {
    await apiService.delete(`/users/hard/${id}`);
  }

  public async getSalaryByUserId(userId: number): Promise<Salary[]> {
    const response = await apiService.get<Salary[]>(`/salary/${userId}`);
    return response;
  }

  public async getUsersBirthdays(companyId: number): Promise<UserBirthdaysResponseDto> {
    const response = await apiService.get<UserBirthdaysResponseDto>(`/users/birthdays/${companyId}`);
    return response;
  }

  public async getYearlyDetails(userId: number): Promise<YearlyDetailsForEmployeeResponseDto> {
    const response = await apiService.get<YearlyDetailsForEmployeeResponseDto>(`/users/annual-report/${userId}`);
    return response;
  }

  public async getYearlyWorkStats(userId: number): Promise<YearlyWorkStatsResponseDto> {
    const response = await apiService.get<YearlyWorkStatsResponseDto>(`/users/work-stats/${userId}`);
    return response;
  }

  public async getWorkSchedule(userId: number): Promise<WorkScheduleResponseDto> {
    const response = await apiService.get<WorkScheduleResponseDto>(`/users/work-schedule/${userId}`);
    return response;
  }

  public async getUserByEmail(email: string): Promise<User> {
    const response = await apiService.get<User>(`/users/email/${email}`);
    return response;
  }

  public async updateSalary(userId: number, newAmount: number): Promise<SalaryUpdateDTO> {
    const response = await apiService.post<SalaryUpdateDTO>(`/salary/update-salary`, { amount: newAmount, userId });
    return response;
  }

  public async resetDevice(targetUserId: number): Promise<void> {
    await apiService.post(`/auth/reset-device`, { targetUserId });
  }

  public async getDeviceConflictLogs(): Promise<any[]> {
    const response = await apiService.get<any[]>(`/auth/device-conflicts`);
    return response || [];
  }

  public async getForgottenCheckouts(): Promise<ForgottenCheckoutDto[]> {
    const response = await apiService.get<ForgottenCheckoutDto[]>(`/daily-records/forgotten-checkouts`);
    return response;
  }
  public async getForgottenCheckins(checkTime?: string): Promise<ForgottenCheckinDto[]> {
    const queryParams = checkTime ? `?checkTime=${checkTime}` : '';
    const response = await apiService.get<ForgottenCheckinDto[]>(`/daily-records/forgotten-checkins${queryParams}`);
    return response;
  }

  public async updateUserPassword(id: number, password: string): Promise<User> {
    const response = await apiService.patch<User>(`/users/${id}/password`, { password });
    return response;
  }

  /**
   * Update user leave limits
   * @param id User ID
   * @param leaveLimits Leave limits object
   */
  public async updateUserLeaveLimits(id: number, leaveLimits: any): Promise<User> {
    const response = await apiService.patch<User>(`/users/${id}/leave-limits`, { leaveLimits });
    return response;
  }

  /**
   * Şirketteki hibrit çalışanları, saha modu otomatik onay durumlarıyla listeler
   */
  public async getHybridEmployees(): Promise<User[]> {
    const response = await apiService.get<User[]>(`/users/hybrid-employees`);
    return response;
  }

  /**
   * Kişi bazlı: bu çalışanın saha modu geçiş talepleri otomatik onaylansın mı
   */
  public async setFieldModeAutoApprove(id: number, enabled: boolean): Promise<User> {
    const response = await apiService.patch<User>(`/users/${id}/field-mode-auto-approve`, { enabled });
    return response;
  }

  /**
   * Get users eligible to be leave approvers (admins and managers)
   */
  public async getEligibleApprovers(): Promise<EligibleApprover[]> {
    const response = await apiService.get<EligibleApprover[]>(`/users/eligible-approvers`);
    return response;
  }
}

export default UserService.getInstance();